
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ConsultModal from '../../components/feature/ConsultModal';
import ScrollToTop from '../../components/feature/ScrollToTop';
import LanguageGuard from '../../components/feature/LanguageGuard';
import SeoHead from '../../components/feature/SeoHead';
import { getLangPath, type Language } from '../../router/langRoutes';

// Home sections
import HomeHeroSection from '../home/components/HeroSection';
import HomeBenefitsSection from '../home/components/BenefitsSection';
import HomeProcessSection from '../home/components/ProcessSection';
import PackagesSection from '../home/components/PackagesSection';
import FAQSection from '../home/components/FAQSection';
import StatsSection from '../home/components/StatsSection';
import CTASection from '../home/components/CTASection';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://thedaeg.com';

const seoData: Record<string, { title: string; description: string; keywords: string }> = {
  vi: {
    title: 'Định Cư Latvia 2025 - Điều Kiện, Quy Trình & Chi Phí Đầu Tư | The Daeg International',
    description:
      'Hướng dẫn chi tiết về định cư Latvia 2025: Điều kiện đầu tư từ 50.000 EUR, quy trình 4-6 tháng, quyền lợi thẻ tạm trú Schengen. Đầu tư vào Asian Alley - chuỗi siêu thị châu Á hàng đầu tại Riga, Latvia. Tư vấn miễn phí cho người Việt Nam.',
    keywords:
      'định cư Latvia 2025, điều kiện định cư Latvia, chi phí định cư Latvia, quy trình định cư Latvia, đầu tư Asian Alley, thẻ tạm trú Schengen, định cư Riga',
  },
  en: {
    title: 'Latvia Residency 2025 - Investment Requirements, Process & Costs | The Daeg International',
    description:
      'Detailed guide on Latvia residency 2025: Investment from €50,000, 4-6 month process, Schengen residence permit benefits. Invest in Asian Alley supermarket chain in Riga. Free consultation for Vietnamese.',
    keywords:
      'Latvia residency 2025, Latvia investment immigration, Latvia residence permit, Schengen card Latvia, Asian Alley investment, Latvia immigration cost',
  },
  lv: {
    title: 'Latvijas Uzturēšanās Atļauja 2025 - Prasības & Izmaksas | The Daeg International',
    description:
      'Detalizēta informācija par uzturēšanās atļauju Latvijā 2025: ieguldījums no 50 000 EUR, 4-6 mēnešu process, Šengenas uzturēšanās atļaujas priekšrocības.',
    keywords:
      'Latvijas uzturēšanās atļauja, ieguldījumi Latvijā, Asian Alley Rīga, imigrācija Latvijā',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Vì sao nên chọn định cư Latvia?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Latvia là quốc gia thuộc Liên minh Châu Âu (EU) và khối Schengen. Khi có thẻ cư trú Latvia, bạn được tự do đi lại 29 quốc gia Schengen, quyền sinh sống hợp pháp tại Châu Âu, cơ hội cho con học tập tại EU với chi phí thấp và chi phí đầu tư thấp so với các chương trình định cư EU khác.',
      },
    },
    {
      '@type': 'Question',
      name: 'Khoản đầu tư 50.000 EUR có bị mất không?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Khoản 50.000 EUR là khoản tham gia vào chương trình để đủ điều kiện xin thẻ cư trú tạm thời Latvia. Sau thời gian duy trì tối thiểu 5 năm, tùy theo tình hình hoạt động, bạn có thể được hoàn lại khoảng 10.000 – 15.000 EUR.',
      },
    },
    {
      '@type': 'Question',
      name: 'Sau bao lâu tôi nhận được thẻ cư trú?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Thông thường thời gian xử lý 6 – 12 tháng kể từ khi hoàn tất hồ sơ và đầu tư.',
      },
    },
    {
      '@type': 'Question',
      name: 'Gia đình tôi có thể đi cùng không?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Có. Chương trình cho phép bao gồm vợ/chồng và con cái phụ thuộc. Cả gia đình đều được cấp thẻ cư trú Latvia cùng lúc.',
      },
    },
    {
      '@type': 'Question',
      name: 'Tôi có cần sống ở Latvia không?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Không bắt buộc phải sống toàn thời gian tại Latvia. Bạn vẫn có thể sinh sống tại Việt Nam và đi lại Châu Âu khi cần, trong khi vẫn duy trì thẻ cư trú hợp pháp.',
      },
    },
  ],
};

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dịch vụ Đầu Tư Định Cư Latvia',
  provider: {
    '@type': 'Organization',
    name: 'The Daeg International Trade',
    url: SITE_URL,
  },
  description:
    'Dịch vụ tư vấn và hỗ trợ đầu tư định cư Latvia thông qua siêu thị Asian Alley tại Riga. Đầu tư từ 50.000 EUR, nhận thẻ tạm trú Schengen trong 4-6 tháng.',
  areaServed: [
    { '@type': 'Country', name: 'Vietnam' },
    { '@type': 'Country', name: 'Latvia' },
  ],
  offers: {
    '@type': 'Offer',
    price: '50000',
    priceCurrency: 'EUR',
    description: 'Đầu tư định cư Latvia từ 50.000 EUR',
  },
};

const LatviaPage = () => {
  const { i18n } = useTranslation('latvia');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const lang = (i18n.language in seoData ? i18n.language : 'vi') as Language;
  const seo = seoData[lang];

  return (
    <LanguageGuard>
      <div className="min-h-screen bg-white">
        <SeoHead
          title={seo.title}
          description={seo.description}
          keywords={seo.keywords}
          canonicalPath={getLangPath('latvia', lang)}
          schemaJson={[faqSchema, serviceSchema]}
        />
        <header>
          <Navbar onConsultClick={() => setIsModalOpen(true)} />
        </header>

        <main>
          <HomeHeroSection onConsultClick={() => setIsModalOpen(true)} />
          <HomeBenefitsSection />
          <HomeProcessSection />
          <PackagesSection onConsultClick={() => setIsModalOpen(true)} />
          <FAQSection />
          <StatsSection />
          <CTASection onConsultClick={() => setIsModalOpen(true)} />
        </main>

        <Footer />
        <ConsultModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        <ScrollToTop />
      </div>
    </LanguageGuard>
  );
};

export default LatviaPage;
